import React, { useCallback } from "react";
import { gql, useQuery } from "@apollo/client";
import {
  FindAllAuditLogsQueryQuery,
  FindAllAuditLogsQueryQueryVariables
} from "../../../../schemaTypes";

export const findAllAuditLogsQuery = gql`
  query FindAllAuditLogsQuery(
    $duration: String
    $searchText: String
    $page: Int!
    $pageSize: Int!
    $sortBy: String
    $sortOrder: String
  ) {
    findAllAuditLogs(
      duration: $duration
      searchText: $searchText
      page: $page
      pageSize: $pageSize
      sortBy: $sortBy
      sortOrder: $sortOrder
    ) {
      data {
        id
        entity
        action
        entityId
        ipAddress
        userAgent
        createdAt
      }
      uniqueEntities
      totalLogs
      total
      page
      pageSize
      totalPages
    }
  }
`;

export interface WithFindAllAuditLogs {
  data: FindAllAuditLogsQueryQuery["findAllAuditLogs"]["data"];
  uniqueEntities: number;
  totalLogs: number;
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  loading: boolean;
  loadMore: () => void;
  hasMoreAuditLogs: boolean;
}

interface Props {
  variables: FindAllAuditLogsQueryQueryVariables;
  children: (data: WithFindAllAuditLogs) => React.ReactElement | null;
}

export const FindAllAuditLogsController: React.FC<Props> = ({ children, variables }) => {
  const { data, loading, fetchMore } = useQuery<
    FindAllAuditLogsQueryQuery,
    FindAllAuditLogsQueryQueryVariables
  >(findAllAuditLogsQuery, {
    variables,
    notifyOnNetworkStatusChange: true,
  });

  const findAllAuditLogsData = data?.findAllAuditLogs?.data || [];
  const uniqueEntities = data?.findAllAuditLogs?.uniqueEntities || 0;
  const totalLogs = data?.findAllAuditLogs?.totalLogs || 0;
  const page = data?.findAllAuditLogs?.page || variables.page;
  const pageSize = data?.findAllAuditLogs?.pageSize || variables.pageSize;
  const total = data?.findAllAuditLogs?.total || 0;
  const totalPages = data?.findAllAuditLogs?.totalPages || 0;
  const hasMoreAuditLogs = page < totalPages;

  const loadMore = useCallback(() => {
    if (!hasMoreAuditLogs) return;
    fetchMore({
      variables: {
        ...variables,
        page: page + 1
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult || !fetchMoreResult.findAllAuditLogs) {
          return prev;
        }
        return {
          ...fetchMoreResult,
          findAllAuditLogs: {
            ...fetchMoreResult.findAllAuditLogs,
            data: [
              ...(prev?.findAllAuditLogs?.data || []),
              ...(fetchMoreResult.findAllAuditLogs.data || [])
            ]
          }
        };
      }
    });
  }, [fetchMore, variables, page, hasMoreAuditLogs]);

  return children({
    data: findAllAuditLogsData as any,
    uniqueEntities,
    totalLogs,
    loading,
    loadMore,
    hasMoreAuditLogs,
    page,
    pageSize,
    total,
    totalPages,
  });
};